import { useEffect, useMemo, useState } from "react";
import { formatBytes, formatRelativeTime } from "@saucer/core";
import type { OwnerStats, StatsDay } from "@saucer/core";
import { loadStats } from "../api.js";
import {
  Chart,
  DAY_LABELS,
  HOUR_LABELS,
  Meters,
  RANGES,
  Sparks,
  Tile,
  Toggle,
  flagFor,
} from "../components/charts.js";
import type { Metric } from "../components/charts.js";

/**
 * Everything on this page arrives in one `GET /api/stats` — a single KV read of
 * a blob the cron trigger built off the Iceberg tables. So the range toggle and
 * the metric toggle re-slice what is already here rather than asking again: no
 * interaction on this page costs a round trip.
 */

export function Stats() {
  const [stats, setStats] = useState<OwnerStats | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "pending" | "off" | "error">("loading");
  const [days, setDays] = useState<number>(30);
  const [metric, setMetric] = useState<Metric>("opens");

  useEffect(() => {
    loadStats()
      .then((body) => {
        setStats(body.stats);
        setState(body.stats ? "ready" : body.lakehouse ? "pending" : "off");
      })
      .catch(() => setState("error"));
  }, []);

  const window = useMemo(
    () => (stats ? stats.daily.slice(-days) : []),
    [stats, days],
  );

  if (state === "loading") return <Shell><p className="meta">Loading…</p></Shell>;
  if (state === "error")
    return (
      <Shell>
        <p className="notice notice--error">Stats are unavailable right now.</p>
      </Shell>
    );
  if (state === "off")
    return (
      <Shell>
        <p className="notice">This deployment has no R2 lakehouse wired up, so there is nothing to count yet.</p>
      </Shell>
    );
  if (state === "pending" || !stats)
    return (
      <Shell>
        <p className="notice">
          Your first rollup has not run yet. Stats are rebuilt every 15 minutes — check back shortly.
        </p>
      </Shell>
    );

  const { totals } = stats;
  const windowTotals = window.reduce(
    (sum, day) => ({
      views: sum.views + day.views,
      downloads: sum.downloads + day.downloads,
      uploads: sum.uploads + day.uploads,
      bytes: sum.bytes + day.bytes,
    }),
    { views: 0, downloads: 0, uploads: 0, bytes: 0 },
  );
  const busiest = window.reduce<StatsDay | null>(
    (best, day) => (!best || day.views + day.downloads > best.views + best.downloads ? day : best),
    null,
  );

  return (
    <main className="stack narrow">
      <div className="section-head">
        <h1>Stats</h1>
        <span className="meta">
          {stats.usage.files} files · {formatBytes(stats.usage.bytes)} · built{" "}
          {formatRelativeTime(stats.generatedAt)}
        </span>
      </div>

      <section className="tiles">
        <Tile label="Views" value={totals.views} />
        <Tile label="Downloads" value={totals.downloads} />
        <Tile label="Uploads" value={totals.uploads} />
        <Tile label="Served" value={formatBytes(totals.bytesServed)} />
        <Tile label="Files opened" value={totals.filesSeen} hint="approx." />
        <Tile label="Countries" value={totals.countriesSeen} hint="approx." />
        <Tile label="Conversions" value={totals.transforms} />
        <Tile label="Events" value={totals.events} />
      </section>

      <section className="stack stack--tight">
        <div className="section-head">
          <h2>Activity</h2>
          <div className="toggles">
            <Toggle
              options={[
                { key: "opens", label: "opens" },
                { key: "bytes", label: "bytes" },
              ]}
              active={metric}
              onPick={(key) => setMetric(key as Metric)}
            />
            <Toggle
              options={RANGES.map((range) => ({ key: String(range.days), label: range.label }))}
              active={String(days)}
              onPick={(key) => setDays(Number(key))}
            />
          </div>
        </div>

        <Chart days={window} metric={metric} />

        {metric === "opens" && (
          <p className="meta legend">
            <span className="legend__key legend__key--views" /> views
            <span className="legend__key legend__key--downloads" /> downloads
          </p>
        )}

        <p className="meta">
          {windowTotals.views.toLocaleString()} views · {windowTotals.downloads.toLocaleString()}{" "}
          downloads · {windowTotals.uploads.toLocaleString()} uploads ·{" "}
          {formatBytes(windowTotals.bytes)} served
          {busiest && busiest.views + busiest.downloads > 0
            ? ` · busiest ${busiest.day} (${busiest.views + busiest.downloads})`
            : ""}
        </p>
      </section>

      <section className="stack stack--tight">
        <h2>Most opened</h2>
        {stats.topFiles.length > 0 ? (
          <div className="scroller"><table className="grid">
            <thead>
              <tr>
                <th>File</th>
                <th className="grid__num">Views</th>
                <th className="grid__num">Downloads</th>
                <th className="grid__num">Last opened</th>
              </tr>
            </thead>
            <tbody>
              {stats.topFiles.map((file) => (
                <tr key={file.slug}>
                  <td className="mono grid__name">{file.slug}</td>
                  <td className="grid__num">{file.views.toLocaleString()}</td>
                  <td className="grid__num">{file.downloads.toLocaleString()}</td>
                  <td className="grid__num meta">
                    {file.lastHit ? formatRelativeTime(file.lastHit) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : (
          <p className="meta">Nothing has been opened yet.</p>
        )}
      </section>

      <div className="panes">
        <section className="stack stack--tight">
          <h2>Where from</h2>
          <Meters
            rows={stats.countries.map((row) => ({
              label: `${flagFor(row.country)} ${row.country || "unknown"}`,
              hits: row.hits,
            }))}
            empty="No opens with a country yet."
          />
        </section>

        <section className="stack stack--tight">
          <h2>Referrers</h2>
          <Meters rows={stats.referrers} empty="Every open so far was a direct link." />
        </section>

        <section className="stack stack--tight">
          <h2>Platforms</h2>
          <Meters rows={stats.platforms} empty="No opens yet." />
        </section>

        <section className="stack stack--tight">
          <h2>By kind</h2>
          <Meters
            rows={stats.kinds.map((row) => ({ label: row.kind || "unknown", hits: row.events }))}
            empty="No events yet."
          />
        </section>
      </div>

      <section className="stack stack--tight">
        <h2>Rhythm</h2>
        <p className="meta">When your links get opened, in UTC.</p>
        <Sparks values={stats.hourly} labels={HOUR_LABELS} caption="Hour of day" />
        <Sparks values={stats.weekday} labels={DAY_LABELS} caption="Day of week" />
      </section>

      <section className="stack stack--tight">
        <h2>Library</h2>
        {stats.library.length > 0 ? (
          <div className="scroller"><table className="grid">
            <thead>
              <tr>
                <th>Kind</th>
                <th className="grid__num">Files</th>
                <th className="grid__num">Private</th>
                <th className="grid__num">Size</th>
              </tr>
            </thead>
            <tbody>
              {stats.library.map((row) => (
                <tr key={row.kind}>
                  <td className="mono grid__name">{row.kind}</td>
                  <td className="grid__num">{row.files.toLocaleString()}</td>
                  <td className="grid__num">{row.private.toLocaleString()}</td>
                  <td className="grid__num">{formatBytes(row.bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        ) : (
          <p className="meta">Nothing uploaded yet.</p>
        )}
        {stats.sizes && (
          <p className="meta">
            Upload size — median {formatBytes(stats.sizes.medianBytes)}, p95{" "}
            {formatBytes(stats.sizes.p95Bytes)}, largest {formatBytes(stats.sizes.maxBytes)}.
          </p>
        )}
      </section>

      <section className="stack stack--tight">
        <h2>Latest</h2>
        {stats.feed.length > 0 ? (
          <ul className="feed">
            {stats.feed.map((event, index) => (
              <li key={`${event.at}-${index}`} className="feed__row">
                <span className={`chip chip--${event.type}`}>{event.type}</span>
                <span className="mono feed__slug">{event.slug}</span>
                <span className="meta">{event.country || "—"}</span>
                <span className="meta">{formatRelativeTime(event.at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="meta">Nothing has happened yet.</p>
        )}
      </section>

      <p className="meta">
        Counted from the R2 Data Catalog, rebuilt every 15 minutes. Distinct counts are sketches, so
        they are approximate by a percent or so.
        {totals.firstEvent ? ` History starts ${totals.firstEvent.slice(0, 10)}.` : ""}
      </p>
    </main>
  );
}

// ---- pieces ----------------------------------------------------------------

const Shell = ({ children }: { children: React.ReactNode }) => (
  <main className="stack narrow">
    <div className="section-head">
      <h1>Stats</h1>
    </div>
    {children}
  </main>
);

